import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Rocket,
  Check,
  ArrowLeft,
  Building2,
  Mail,
  Lock,
  User,
  Loader2,
  Phone,
  MapPin,
  Globe,
  Package,
  Users,
  Calculator,
  Truck,
  Shield,
  Settings,
  Sparkles,
  Eye,
  EyeOff,
  AlertCircle,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Module category icons and colors
const categoryConfig = {
  core: { icon: Package, color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  hr: { icon: Users, color: 'bg-blue-100 text-blue-700 border-blue-200' },
  finance: { icon: Calculator, color: 'bg-amber-100 text-amber-700 border-amber-200' },
  operations: { icon: Truck, color: 'bg-purple-100 text-purple-700 border-purple-200' },
  compliance: { icon: Shield, color: 'bg-red-100 text-red-700 border-red-200' },
  integrations: { icon: Settings, color: 'bg-cyan-100 text-cyan-700 border-cyan-200' },
  general: { icon: Sparkles, color: 'bg-gray-100 text-gray-700 border-gray-200' },
};

// Common TLD typos
const TLD_TYPOS = {
  'comm': 'com', 'con': 'com', 'cpm': 'com', 'vom': 'com', 'xom': 'com',
  'ocm': 'com', 'comn': 'com', 'coim': 'com', 'coom': 'com',
  'orgn': 'org', 'orgg': 'org', 'nett': 'net', 'ent': 'net',
};

// Common domain typos
const DOMAIN_TYPOS = {
  'gmial.com': 'gmail.com', 'gmal.com': 'gmail.com', 'gamil.com': 'gmail.com',
  'gnail.com': 'gmail.com', 'gmaik.com': 'gmail.com', 'gmail.comm': 'gmail.com',
  'hotmal.com': 'hotmail.com', 'hotmial.com': 'hotmail.com', 'hotmail.comm': 'hotmail.com',
  'outloo.com': 'outlook.com', 'outlok.com': 'outlook.com', 'outlook.comm': 'outlook.com',
  'yahooo.com': 'yahoo.com', 'yaho.com': 'yahoo.com', 'yahoo.comm': 'yahoo.com',
};

const validateEmail = (email) => {
  if (!email) return { valid: false, error: 'Email is required' };
  
  const emailLower = email.trim().toLowerCase();
  
  if (!emailLower.includes('@') || !emailLower.includes('.')) {
    return { valid: false, error: 'Please enter a valid email address' };
  }
  
  const [localPart, domain] = emailLower.split('@');
  
  if (!localPart || !domain) {
    return { valid: false, error: 'Please enter a valid email address' };
  }
  
  if (DOMAIN_TYPOS[domain]) {
    const suggested = `${localPart}@${DOMAIN_TYPOS[domain]}`;
    return { valid: false, error: `Did you mean ${suggested}?`, suggestion: suggested };
  }
  
  const domainParts = domain.split('.');
  if (domainParts.length >= 2) {
    const tld = domainParts[domainParts.length - 1];
    if (TLD_TYPOS[tld]) {
      const correctedDomain = [...domainParts.slice(0, -1), TLD_TYPOS[tld]].join('.');
      const suggested = `${localPart}@${correctedDomain}`;
      return { valid: false, error: `Did you mean ${suggested}? '.${tld}' looks like a typo.`, suggestion: suggested };
    }
  }
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(emailLower)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }
  
  return { valid: true };
};

export default function Signup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [resetSending, setResetSending] = useState(false);
  
  // Plans state (Phase 19.8: replaces legacy modular module-picker)
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlanId, setSelectedPlanId] = useState('business');
  const [billingCycle, setBillingCycle] = useState('monthly'); // monthly | yearly
  const [loadingPlans, setLoadingPlans] = useState(false);

  // Phase F.6 — Compliance: explicit Terms + Privacy acceptance at signup.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [legalVersions, setLegalVersions] = useState({ terms: null, privacy: null });
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    phone: '',
    address: '',
    country: '',
    postal_code: '',
    business_name: '',
  });

  // Fetch available plans on mount (Phase 19.8: aligns with /pricing page)
  useEffect(() => {
    fetchAvailablePlans();
  }, []);

  // Phase F.6 — fetch the current Terms + Privacy version so we can record exactly
  // which doc version the user clicked through.
  useEffect(() => {
    const fetchVersions = async () => {
      try {
        const [terms, privacy] = await Promise.all([
          fetch(`${API_URL}/api/legal/terms`).then(r => r.ok ? r.json() : null),
          fetch(`${API_URL}/api/legal/privacy`).then(r => r.ok ? r.json() : null),
        ]);
        setLegalVersions({
          terms: terms?.version ?? null,
          privacy: privacy?.version ?? null,
        });
      } catch { /* non-fatal */ }
    };
    fetchVersions();
  }, []);

  const fetchAvailablePlans = async () => {
    setLoadingPlans(true);
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/plans`);
      if (response.ok) {
        const data = await response.json();
        // Show Business + Enterprise only; hide Corporate (under_construction)
        const selectable = (Array.isArray(data) ? data : data.plans || [])
          .filter((p) => !p.under_construction);
        setAvailablePlans(selectable);
        // Default to Business if available
        if (selectable.length > 0 && !selectable.find((p) => p.id === selectedPlanId)) {
          setSelectedPlanId(selectable[0].id);
        }
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    if (name === 'email') {
      setEmailSuggestion(null);
      setEmailExists(false);
    }
  };

  // Phase 19.19 — debounced duplicate-email check
  useEffect(() => {
    const email = formData.email.trim().toLowerCase();
    if (!email || !email.includes('@') || !email.includes('.')) {
      setEmailExists(false);
      return;
    }
    const handle = setTimeout(async () => {
      setCheckingEmail(true);
      try {
        const res = await axios.post(`${API_URL}/api/auth/check-email`, { email });
        setEmailExists(Boolean(res.data?.exists));
      } catch {
        setEmailExists(false);
      } finally {
        setCheckingEmail(false);
      }
    }, 500);
    return () => clearTimeout(handle);
  }, [formData.email]);

  const handleSendResetLink = async () => {
    if (!formData.email) return;
    setResetSending(true);
    try {
      await axios.post(`${API_URL}/api/auth/forgot-password`, {
        email: formData.email.trim().toLowerCase(),
      });
      toast.success('Password reset link sent! Check your inbox.');
    } catch {
      toast.success('If an account exists, a reset link has been sent.');
    } finally {
      setResetSending(false);
    }
  };

  const acceptEmailSuggestion = () => {
    if (emailSuggestion) {
      setFormData({ ...formData, email: emailSuggestion });
      setEmailSuggestion(null);
    }
  };

  const toggleModule = (moduleId) => {
    // Phase 19.8: legacy helper kept for back-compat (no-op after plan refactor)
    void moduleId;
  };

  const calculateTotalPrice = () => {
    // Phase 19.8: derive from the selected plan + billing cycle
    const plan = availablePlans.find((p) => p.id === selectedPlanId);
    if (!plan) return 0;
    if (billingCycle === 'yearly') {
      // Backend returns `price_year`; tolerate other naming variants too.
      return (
        plan.price_year ?? plan.price_yearly ?? plan.yearly_price ?? (plan.price ?? 0) * 12 * 0.85
      );
    }
    return plan.price ?? plan.price_monthly ?? 0;
  };

  const validateStep1 = () => {
    if (!formData.email || !formData.password || !formData.confirmPassword) {
      toast.error('Please fill in all fields');
      return false;
    }
    
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.valid) {
      toast.error(emailValidation.error);
      if (emailValidation.suggestion) {
        setEmailSuggestion(emailValidation.suggestion);
      }
      return false;
    }
    
    if (emailExists) {
      toast.error('This email is already registered. Please sign in or reset your password.');
      return false;
    }
    
    if (formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.first_name || !formData.last_name || !formData.phone) {
      toast.error('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const validateStep3 = () => {
    if (!formData.address || !formData.country || !formData.postal_code || !formData.business_name) {
      toast.error('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const validateStep4 = () => {
    // Phase 19.8: A selectable plan must be chosen.
    if (!selectedPlanId) {
      toast.error('Please choose a plan');
      return false;
    }
    if (!availablePlans.find((p) => p.id === selectedPlanId)) {
      toast.error('Selected plan is no longer available');
      return false;
    }
    // Phase F.6 — Compliance: must explicitly accept Terms + Privacy.
    if (!termsAccepted) {
      toast.error('Please agree to the Terms of Service and Privacy Policy to continue.');
      return false;
    }
    return true;
  };

  const handleSignup = async () => {
    if (!validateStep4()) return;

    setLoading(true);

    // Helper to surface the most useful error message from an axios error
    const extractDetail = (err, fallback) => {
      const data = err?.response?.data;
      let detail = data?.detail ?? data?.message ?? null;
      if (Array.isArray(detail) && detail.length > 0) {
        detail = detail[0]?.msg || detail[0]?.message || JSON.stringify(detail[0]);
      } else if (detail && typeof detail === 'object') {
        detail = detail.message || JSON.stringify(detail);
      }
      const status = err?.response?.status;
      return detail || (status ? `${fallback} (HTTP ${status})` : fallback);
    };

    try {
      // Step 1: Create account with selected plan (Phase 19.8 — use axios to bypass
      // the platform's window.fetch wrapper that pre-reads response bodies).
      let signupData;
      try {
        const res = await axios.post(`${API_URL}/api/subscriptions/signup`, {
          email: formData.email,
          password: formData.password,
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          address: formData.address,
          country: formData.country,
          postal_code: formData.postal_code,
          business_name: formData.business_name,
          plan: selectedPlanId,
          terms_accepted: termsAccepted,
          terms_version: legalVersions.terms,
          privacy_version: legalVersions.privacy,
        });
        signupData = res.data;
      } catch (err) {
        throw new Error(
          extractDetail(
            err,
            'Signup failed. Please double-check your details and try again.'
          )
        );
      }

      localStorage.setItem('token', signupData.access_token);
      localStorage.setItem('user', JSON.stringify(signupData.user));

      // No-CC free trial (30 days) — head straight to the dashboard.
      toast.success('Welcome! Your 30-day free trial has started — no credit card required.');
      setRedirecting(true);
      setLoading(false);
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 800);
      return;
    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error.message || 'Signup failed. Please try again.';
      if (errorMessage.toLowerCase().includes('already registered')) {
        // Phase 19.10: one-tap recovery via toast action button.
        toast.error('This email is already registered.', {
          description: 'Sign in or use a different email to continue.',
          action: {
            label: 'Sign in',
            onClick: () => navigate(`/login?email=${encodeURIComponent(formData.email)}`),
          },
          duration: 10000,
        });
      } else {
        toast.error(errorMessage);
      }
      setLoading(false);
    }
  };

  // Show redirecting state
  if (redirecting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-cyan-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Setting up your workspace</h2>
          <p className="text-slate-400">Your 30-day free trial has started — no credit card required.</p>
        </div>
      </div>
    );
  }

  const totalSteps = 4;
  const totalPrice = calculateTotalPrice();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-xl">
        {/* Logo */}
        <div className="flex items-center justify-center gap-2 mb-8">
          <img
            src="/business-genie-logo.png"
            alt="Business Genie"
            className="h-12 w-12 rounded-xl object-contain bg-white/5"
            data-testid="signup-bg-logo"
          />
          <span className="text-2xl font-bold text-white">Business Genie</span>
        </div>

        <Card className="bg-slate-800/50 border-slate-700 backdrop-blur-sm">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl text-white">Create your account</CardTitle>
            <CardDescription className="text-slate-400">
              {step === 1 && 'Enter your credentials'}
              {step === 2 && 'Personal information'}
              {step === 3 && 'Business details'}
              {step === 4 && 'Choose your plan'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Step Indicator - 4 steps */}
            <div className="flex items-center justify-center gap-2">
              {[1, 2, 3, 4].map((s, i) => (
                <React.Fragment key={s}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step >= s ? 'bg-cyan-500 text-white' : 'bg-slate-700 text-slate-400'
                  }`}>
                    {step > s ? <Check className="h-4 w-4" /> : s}
                  </div>
                  {i < 3 && <div className={`w-8 h-1 ${step > s ? 'bg-cyan-500' : 'bg-slate-700'}`} />}
                </React.Fragment>
              ))}
            </div>

            {/* Step 1: Credentials */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-300">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@company.com"
                      className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                      value={formData.email}
                      onChange={handleInputChange}
                      data-testid="signup-email-input"
                    />
                  </div>
                  {emailSuggestion && (
                    <div className="flex items-center gap-2 p-2 bg-amber-500/20 border border-amber-500/30 rounded-lg">
                      <span className="text-sm text-amber-300">
                        Did you mean <strong>{emailSuggestion}</strong>?
                      </span>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="h-7 text-amber-300 hover:text-amber-100 hover:bg-amber-500/30"
                        onClick={acceptEmailSuggestion}
                      >
                        Yes, use this
                      </Button>
                    </div>
                  )}
                  {checkingEmail && !emailExists && (
                    <p
                      className="text-xs text-slate-500 flex items-center gap-1.5"
                      data-testid="signup-email-checking"
                    >
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Checking availability…
                    </p>
                  )}
                  {emailExists && (
                    <div
                      className="flex flex-col gap-2 p-3 bg-rose-500/15 border border-rose-500/30 rounded-lg"
                      data-testid="signup-email-exists-warning"
                    >
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-rose-300 mt-0.5 shrink-0" />
                        <p className="text-sm text-rose-200 leading-snug">
                          <strong>This email is already registered.</strong> Sign in or reset your password to continue.
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          className="h-8 bg-cyan-500 hover:bg-cyan-600 text-white"
                          onClick={() => navigate(`/login?email=${encodeURIComponent(formData.email)}`)}
                          data-testid="signup-email-exists-signin-btn"
                        >
                          Sign in
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-8 border-rose-400/40 text-rose-200 hover:bg-rose-500/20"
                          onClick={handleSendResetLink}
                          disabled={resetSending}
                          data-testid="signup-email-exists-reset-btn"
                        >
                          {resetSending ? (
                            <>
                              <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />
                              Sending…
                            </>
                          ) : (
                            'Send reset link'
                          )}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-300">Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      className="pl-10 pr-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                      value={formData.password}
                      onChange={handleInputChange}
                      data-testid="signup-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-400 transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      data-testid="signup-password-toggle"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword" className="text-slate-300">Confirm Password *</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showConfirmPassword ? 'text' : 'password'}
                      placeholder="Confirm your password"
                      className="pl-10 pr-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      data-testid="signup-confirm-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-cyan-400 transition-colors"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      data-testid="signup-confirm-password-toggle"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                  onClick={() => validateStep1() && setStep(2)}
                  data-testid="signup-step1-continue-btn"
                >
                  Continue
                </Button>
              </div>
            )}

            {/* Step 2: Personal Information */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-slate-300">First Name *</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <Input
                        id="first_name"
                        name="first_name"
                        placeholder="John"
                        className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                        value={formData.first_name}
                        onChange={handleInputChange}
                        data-testid="signup-first-name-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-slate-300">Last Name *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      placeholder="Smith"
                      className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      data-testid="signup-last-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-300">Phone Number *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                      value={formData.phone}
                      onChange={handleInputChange}
                      data-testid="signup-phone-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-700"
                    onClick={() => setStep(1)}
                    data-testid="signup-step2-back-btn"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                    onClick={() => validateStep2() && setStep(3)}
                    data-testid="signup-step2-continue-btn"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Business & Address */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="business_name" className="text-slate-300">Business Name *</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="business_name"
                      name="business_name"
                      placeholder="Acme Industries Ltd."
                      className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                      value={formData.business_name}
                      onChange={handleInputChange}
                      data-testid="signup-business-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="address" className="text-slate-300">Street Address *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                    <Input
                      id="address"
                      name="address"
                      placeholder="123 Main Street, Suite 100"
                      className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                      value={formData.address}
                      onChange={handleInputChange}
                      data-testid="signup-address-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country" className="text-slate-300">Country *</Label>
                    <div className="relative">
                      <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                      <Input
                        id="country"
                        name="country"
                        placeholder="Canada"
                        className="pl-10 bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                        value={formData.country}
                        onChange={handleInputChange}
                        data-testid="signup-country-input"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="postal_code" className="text-slate-300">Postal Code *</Label>
                    <Input
                      id="postal_code"
                      name="postal_code"
                      placeholder="A1B 2C3"
                      className="bg-slate-900/50 border-slate-700 text-white placeholder:text-slate-500"
                      value={formData.postal_code}
                      onChange={handleInputChange}
                      data-testid="signup-postal-code-input"
                    />
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-700"
                    onClick={() => setStep(2)}
                    data-testid="signup-step3-back-btn"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                    onClick={() => validateStep3() && setStep(4)}
                    data-testid="signup-step3-continue-btn"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Plan Selection (Phase 19.8 — aligns with /pricing) */}
            {step === 4 && (
              <div className="space-y-4">
                {/* Phase 19.10 — Trust banner */}
                <div
                  className="flex items-center justify-center gap-2 px-3 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-300 text-xs font-medium w-fit mx-auto"
                  data-testid="signup-trial-badge"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>30-day free trial · No credit card required · Cancel anytime</span>
                </div>

                {/* Billing cycle toggle */}
                <div
                  className="flex items-center justify-center gap-2 p-1 bg-slate-900/50 border border-slate-700 rounded-lg w-fit mx-auto"
                  data-testid="billing-cycle-toggle"
                >
                  <button
                    type="button"
                    onClick={() => setBillingCycle('monthly')}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${
                      billingCycle === 'monthly'
                        ? 'bg-cyan-500 text-white shadow'
                        : 'text-slate-400 hover:text-white'
                    }`}
                    data-testid="billing-monthly-btn"
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setBillingCycle('yearly')}
                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                      billingCycle === 'yearly'
                        ? 'bg-cyan-500 text-white shadow'
                        : 'text-slate-400 hover:text-white'
                    }`}
                    data-testid="billing-yearly-btn"
                  >
                    Yearly
                    <Badge className="text-[10px] bg-emerald-500/20 text-emerald-300 border-emerald-500/30 px-1.5 py-0">
                      Save 15%
                    </Badge>
                  </button>
                </div>

                {/* Price Summary */}
                <div className="flex items-center justify-between p-3 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-500/30 rounded-lg">
                  <span className="text-slate-300">
                    {billingCycle === 'yearly' ? 'Yearly Total:' : 'Monthly Total:'}
                  </span>
                  <span className="text-2xl font-bold text-white" data-testid="signup-price-total">
                    ${totalPrice.toFixed(2)}/{billingCycle === 'yearly' ? 'yr' : 'mo'}
                  </span>
                </div>

                {loadingPlans ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
                  </div>
                ) : availablePlans.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Package className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No plans available yet</p>
                    <p className="text-sm">Please try again in a moment.</p>
                  </div>
                ) : (
                  <div className="space-y-3" data-testid="signup-plans-list">
                    {availablePlans.map((plan) => {
                      const isSelected = selectedPlanId === plan.id;
                      const monthlyPrice = plan.price ?? plan.price_monthly ?? 0;
                      const yearlyPrice =
                        plan.price_year ?? plan.price_yearly ?? plan.yearly_price ?? monthlyPrice * 12 * 0.85;
                      const displayPrice =
                        billingCycle === 'yearly' ? yearlyPrice : monthlyPrice;

                      const formatLimit = (n) => (n === -1 ? 'Unlimited' : `up to ${n}`);

                      return (
                        <div
                          key={plan.id}
                          className={`p-4 rounded-lg border cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-cyan-500/20 border-cyan-500/60 ring-2 ring-cyan-500/30'
                              : 'bg-slate-900/50 border-slate-700 hover:border-slate-500'
                          }`}
                          onClick={() => setSelectedPlanId(plan.id)}
                          data-testid={`plan-select-${plan.id}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1">
                              <Checkbox
                                checked={isSelected}
                                className="mt-1 border-slate-500 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500 rounded-full"
                              />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="font-semibold text-white text-base">
                                    {plan.name}
                                  </span>
                                </div>
                                {plan.tagline && (
                                  <p className="text-xs text-slate-400 mt-1">
                                    {plan.tagline}
                                  </p>
                                )}
                                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-[11px] text-slate-400">
                                  <span>
                                    <span className="text-slate-500">Employees:</span>{' '}
                                    {formatLimit(plan.max_employees)}
                                  </span>
                                  <span>
                                    <span className="text-slate-500">Clients:</span>{' '}
                                    {formatLimit(plan.max_clients)}
                                  </span>
                                  <span>
                                    <span className="text-slate-500">Admin Users:</span>{' '}
                                    {formatLimit(plan.max_users)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="text-right whitespace-nowrap">
                              <div className="text-cyan-400 font-bold text-lg">
                                ${displayPrice.toFixed(2)}
                              </div>
                              <div className="text-[11px] text-slate-500">
                                /{billingCycle === 'yearly' ? 'yr' : 'mo'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Add-on Modules teaser */}
                    <div
                      className="p-3 rounded-lg border border-dashed border-slate-700 bg-slate-900/30 flex items-center justify-between gap-3"
                      data-testid="signup-addons-coming-soon"
                    >
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-slate-500" />
                        <div>
                          <p className="text-sm font-medium text-slate-300">Add-on Modules</p>
                          <p className="text-[11px] text-slate-500">
                            Direct deposit, LIVE A/P &amp; A/R, POS sync — bolt them on once available.
                          </p>
                        </div>
                      </div>
                      <Badge className="text-[10px] bg-amber-500/15 text-amber-300 border-amber-500/30 px-2 py-0.5 whitespace-nowrap">
                        Coming Soon
                      </Badge>
                    </div>

                    <p className="text-[11px] text-slate-500 text-center pt-1">
                      Need more scale or multi-entity consolidation?{' '}
                      <a
                        href="/pricing"
                        className="text-cyan-400 hover:underline"
                        data-testid="contact-corporate-link"
                      >
                        Corporate plan
                      </a>{' '}
                      (Coming Soon) is available via Contact Sales.
                    </p>
                  </div>
                )}

                <div className="text-center">
                  <a
                    href="/pricing"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-cyan-400 hover:text-cyan-300 hover:underline transition-colors"
                    data-testid="signup-more-details-link"
                  >
                    More details on plans
                    <ArrowLeft className="h-3.5 w-3.5 rotate-180" />
                  </a>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-700"
                    onClick={() => setStep(3)}
                    data-testid="signup-step4-back-btn"
                  >
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back
                  </Button>
                  <Button
                    className="flex-1 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white"
                    onClick={handleSignup}
                    disabled={loading || !selectedPlanId}
                    data-testid="signup-submit-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Start Free Trial'
                    )}
                  </Button>
                </div>
              </div>
            )}

            {/* Phase F.6 — Terms + Privacy explicit acceptance with audit trail */}
            <div
              className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 flex items-start gap-3"
              data-testid="signup-terms-acceptance-box"
            >
              <Checkbox
                id="terms-accepted"
                checked={termsAccepted}
                onCheckedChange={(v) => setTermsAccepted(!!v)}
                className="mt-1 border-slate-500 data-[state=checked]:bg-cyan-500 data-[state=checked]:border-cyan-500"
                data-testid="signup-terms-checkbox"
              />
              <Label
                htmlFor="terms-accepted"
                className="text-xs text-slate-300 leading-relaxed cursor-pointer select-none"
              >
                I have read and agree to the{' '}
                <a
                  href="/legal/terms"
                  target="_blank"
                  rel="noreferrer"
                  className="text-cyan-400 hover:underline"
                  data-testid="signup-terms-link"
                >
                  Terms of Service
                </a>{' '}
                and{' '}
                <a
                  href="/legal/privacy"
                  target="_blank"
                  rel="noreferrer"
                  className="text-cyan-400 hover:underline"
                  data-testid="signup-privacy-link"
                >
                  Privacy Policy
                </a>
                {legalVersions.terms && legalVersions.privacy ? (
                  <span className="text-slate-500">
                    {' '}(Terms v{legalVersions.terms} · Privacy v{legalVersions.privacy})
                  </span>
                ) : null}
                . By signing up you consent to Business Genie recording your acceptance,
                IP address, and the document versions shown above for compliance audit.
              </Label>
            </div>

            {/* Login Link */}
            <div className="text-center text-sm text-slate-400">
              Already have an account?{' '}
              <button
                onClick={() => navigate('/login')}
                className="text-cyan-400 hover:underline"
              >
                Sign in
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
